import { supabase } from "@/lib/supabaseClient";

export type EventKind = "masterclass" | "orientacion" | "entrenamiento";
export type EventAudience = "todos" | "equipo";

export type CalEvent = {
  id: string;
  title: string;
  description: string | null;
  kind: EventKind;
  startsAt: string;
  durationMinutes: number;
  hostName: string | null;
  /** Foto horizontal (16:9). */
  imageUrl: string | null;
  /** Foto vertical (4:5), tipo afiche. */
  imageVerticalUrl: string | null;
  /** Foto cuadrada (1:1). */
  imageSquareUrl: string | null;
  joinUrl: string | null;
  recordingUrl: string | null;
  recordingSeconds: number | null;
  capacity: number | null;
  audience: EventAudience;
  active: boolean;
  cancelled: boolean;
  /** Personas con reserva. */
  registered: number;
  /** ¿Yo ya reservé? */
  reserved: boolean;
};

export const KIND_META: Record<EventKind, { label: string; filterLabel: string; icon: string; badge: string; dot: string; gradient: string }> = {
  masterclass: {
    label: "Masterclass en vivo",
    filterLabel: "Masterclass en vivo",
    icon: "ri-live-line",
    badge: "bg-violet-50 text-violet-700",
    dot: "#7C3AED",
    gradient: "from-[#0B1F3A] via-[#2a1a6b] to-[#7C3AED]",
  },
  orientacion: {
    label: "Orientación para invitados",
    filterLabel: "Orientación invitados",
    icon: "ri-user-heart-line",
    badge: "bg-emerald-50 text-emerald-700",
    dot: "#20C997",
    gradient: "from-[#0B1F3A] via-[#0f5a52] to-[#20C997]",
  },
  entrenamiento: {
    label: "Entrenamiento interno",
    filterLabel: "Entrenamientos internos",
    icon: "ri-presentation-line",
    badge: "bg-sky-50 text-sky-700",
    dot: "#0EA5E9",
    gradient: "from-[#0B1F3A] via-[#0c4a6e] to-[#0EA5E9]",
  },
};

type EventRow = {
  id: string;
  title: string;
  description: string | null;
  kind: EventKind;
  starts_at: string;
  duration_minutes: number;
  host_name: string | null;
  image_url: string | null;
  image_vertical_url: string | null;
  image_square_url: string | null;
  join_url: string | null;
  recording_url: string | null;
  recording_seconds: number | null;
  capacity: number | null;
  audience: EventAudience;
  active: boolean;
  cancelled: boolean;
};

export async function fetchEvents(): Promise<CalEvent[]> {
  const [evRes, regRes, cntRes] = await Promise.all([
    supabase.from("events").select("*").order("starts_at", { ascending: true }),
    supabase.from("event_registrations").select("event_id, user_id"),
    supabase.rpc("event_attendee_counts"),
  ]);
  if (evRes.error) throw evRes.error;
  const { data: auth } = await supabase.auth.getUser();
  const me = auth.user?.id;
  const mine = new Set(((regRes.data ?? []) as { event_id: string; user_id: string }[]).filter((r) => r.user_id === me).map((r) => r.event_id));
  const counts = new Map<string, number>(((cntRes.data ?? []) as { event_id: string; total: number | string }[]).map((c) => [c.event_id, Number(c.total)]));
  return ((evRes.data ?? []) as EventRow[]).map((e) => ({
    id: e.id,
    title: e.title,
    description: e.description,
    kind: e.kind,
    startsAt: e.starts_at,
    durationMinutes: e.duration_minutes,
    hostName: e.host_name,
    imageUrl: e.image_url,
    imageVerticalUrl: e.image_vertical_url,
    imageSquareUrl: e.image_square_url,
    joinUrl: e.join_url,
    recordingUrl: e.recording_url,
    recordingSeconds: e.recording_seconds,
    capacity: e.capacity,
    audience: e.audience,
    active: e.active,
    cancelled: e.cancelled,
    registered: counts.get(e.id) ?? 0,
    reserved: mine.has(e.id),
  }));
}

export class EventFullError extends Error {
  constructor() {
    super("event_full");
  }
}

export async function reserveEvent(eventId: string): Promise<void> {
  const { error } = await supabase.from("event_registrations").insert({ event_id: eventId });
  if (error) {
    if (error.message.includes("event_full")) throw new EventFullError();
    if (error.code !== "23505") throw error; // ya estaba reservado
  }
}

export async function cancelReservation(eventId: string): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return;
  const { error } = await supabase.from("event_registrations").delete().eq("event_id", eventId).eq("user_id", auth.user.id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Fechas
// ---------------------------------------------------------------------------

export const endTime = (e: Pick<CalEvent, "startsAt" | "durationMinutes">) => new Date(new Date(e.startsAt).getTime() + e.durationMinutes * 60000);
export const isLive = (e: CalEvent, now = Date.now()) => new Date(e.startsAt).getTime() <= now && endTime(e).getTime() > now;
export const isUpcoming = (e: CalEvent, now = Date.now()) => !e.cancelled && endTime(e).getTime() > now;

export function gmtLabel(d: Date): string {
  const part = new Intl.DateTimeFormat("es", { timeZoneName: "shortOffset" }).formatToParts(d).find((p) => p.type === "timeZoneName")?.value ?? "";
  return part.replace("UTC", "GMT");
}

export function formatDateLong(iso: string): string {
  const s = new Date(iso).toLocaleDateString("es", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatTimeRange(e: Pick<CalEvent, "startsAt" | "durationMinutes">): string {
  const d = new Date(e.startsAt);
  const t = d.toLocaleTimeString("es", { hour: "numeric", minute: "2-digit" }).toUpperCase().replace(/\./g, "");
  return `${t} (${gmtLabel(d)}) · ${e.durationMinutes} min`;
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(h > 0 ? 2 : 1, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// ---------------------------------------------------------------------------
// Agregar al calendario (Google / archivo .ics para Apple y Outlook)
// ---------------------------------------------------------------------------

const icsDate = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const icsEscape = (s: string) => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");

function eventDetails(e: CalEvent): string {
  const parts = [e.description ?? "", e.hostName ? `Con: ${e.hostName}` : "", e.joinUrl ? `Enlace: ${e.joinUrl}` : ""].filter(Boolean);
  return parts.join("\n");
}

export function googleCalendarUrl(e: CalEvent): string {
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: e.title,
    dates: `${icsDate(new Date(e.startsAt))}/${icsDate(endTime(e))}`,
    details: eventDetails(e),
  });
  if (e.joinUrl) p.set("location", e.joinUrl);
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

export function downloadIcs(e: CalEvent): void {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Vivelo AI//Calendario//ES",
    "BEGIN:VEVENT",
    `UID:${e.id}@vivelo-ai.com`,
    `DTSTAMP:${icsDate(new Date())}`,
    `DTSTART:${icsDate(new Date(e.startsAt))}`,
    `DTEND:${icsDate(endTime(e))}`,
    `SUMMARY:${icsEscape(e.title)}`,
    `DESCRIPTION:${icsEscape(eventDetails(e))}`,
    ...(e.joinUrl ? [`LOCATION:${icsEscape(e.joinUrl)}`] : []),
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${e.title.replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-").toLowerCase() || "evento"}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// Administración
// ---------------------------------------------------------------------------

export type EventInput = {
  id?: string;
  title: string;
  description: string | null;
  kind: EventKind;
  startsAt: string;
  durationMinutes: number;
  hostName: string | null;
  imageUrl: string | null;
  imageVerticalUrl: string | null;
  imageSquareUrl: string | null;
  joinUrl: string | null;
  recordingUrl: string | null;
  recordingSeconds: number | null;
  capacity: number | null;
  audience: EventAudience;
  active: boolean;
  cancelled: boolean;
};

export async function saveEvent(input: EventInput): Promise<void> {
  const row = {
    title: input.title,
    description: input.description,
    kind: input.kind,
    starts_at: input.startsAt,
    duration_minutes: input.durationMinutes,
    host_name: input.hostName,
    image_url: input.imageUrl,
    image_vertical_url: input.imageVerticalUrl,
    image_square_url: input.imageSquareUrl,
    join_url: input.joinUrl,
    recording_url: input.recordingUrl,
    recording_seconds: input.recordingSeconds,
    capacity: input.capacity,
    audience: input.audience,
    active: input.active,
    cancelled: input.cancelled,
  };
  const { error } = input.id ? await supabase.from("events").update(row).eq("id", input.id) : await supabase.from("events").insert(row);
  if (error) throw error;
}

export async function deleteEvent(id: string): Promise<void> {
  const { error } = await supabase.from("events").delete().eq("id", id);
  if (error) throw error;
}

export type Attendee = { userId: string; fullName: string | null; email: string; registeredAt: string };

export async function fetchAttendees(eventId: string): Promise<Attendee[]> {
  const { data, error } = await supabase.rpc("event_attendees", { p_event_id: eventId });
  if (error) throw error;
  return ((data ?? []) as { user_id: string; full_name: string | null; email: string; registered_at: string }[]).map((r) => ({
    userId: r.user_id,
    fullName: r.full_name,
    email: r.email,
    registeredAt: r.registered_at,
  }));
}

// ---------------------------------------------------------------------------
// Asistencia y panel avanzado
// ---------------------------------------------------------------------------

/** Registra que la persona entró a la reunión ("join") o vio la grabación ("recording"). */
export async function trackEvent(eventId: string, kind: "join" | "recording"): Promise<void> {
  await supabase.rpc("event_track", { p_event_id: eventId, p_kind: kind });
}

export type Pair = { cur: number; prev: number };

export type EventsDashboard = {
  isAdmin: boolean;
  week: Pair;
  completed: Pair;
  attendance: Pair;
  minutes: Pair;
  stats: { live: number; scheduled: number; recordings: number };
  team: { id: string; fullName: string | null; avatarUrl: string | null; attended: number; eligible: number; percent: number }[];
};

export async function fetchEventsDashboard(): Promise<EventsDashboard | null> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const { data, error } = await supabase.rpc("events_dashboard", { p_tz: tz });
  if (error) throw error;
  return (data as EventsDashboard | null) ?? null;
}

// ---------------------------------------------------------------------------
// Fotos del evento (horizontal, vertical y cuadrada)
// ---------------------------------------------------------------------------

export type ImageSlot = "horizontal" | "vertical" | "square";

export const IMAGE_SLOTS: Record<ImageSlot, { label: string; ratio: number; ratioLabel: string; width: number; size: string; usedIn: string }> = {
  horizontal: { label: "Horizontal", ratio: 16 / 9, ratioLabel: "16:9", width: 1280, size: "1280 × 720", usedIn: "Lista de reuniones, banners y grabaciones" },
  vertical: { label: "Vertical", ratio: 4 / 5, ratioLabel: "4:5", width: 960, size: "960 × 1200", usedIn: "Afiche en el detalle del evento" },
  square: { label: "Cuadrada", ratio: 1, ratioLabel: "1:1", width: 800, size: "800 × 800", usedIn: "Calendario y miniaturas" },
};

/** Elige la mejor foto disponible según el orden de preferencia. */
export function pickImage(e: Pick<CalEvent, "imageUrl" | "imageVerticalUrl" | "imageSquareUrl">, order: ImageSlot[]): string | null {
  for (const slot of order) {
    const url = slot === "horizontal" ? e.imageUrl : slot === "vertical" ? e.imageVerticalUrl : e.imageSquareUrl;
    if (url) return url;
  }
  return null;
}

const BUCKET = "event-media";
let cachedOrgId: string | null = null;

async function orgId(): Promise<string> {
  if (cachedOrgId) return cachedOrgId;
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Debes iniciar sesión.");
  const { data } = await supabase.from("profiles").select("organization_id").eq("id", auth.user.id).maybeSingle();
  if (!data?.organization_id) throw new Error("No se encontró tu organización.");
  cachedOrgId = data.organization_id as string;
  return cachedOrgId;
}

export async function uploadEventImage(slot: ImageSlot, blob: Blob): Promise<string> {
  const org = await orgId();
  const ext = blob.type === "image/webp" ? "webp" : blob.type === "image/png" ? "png" : "jpg";
  const path = `${org}/${crypto.randomUUID()}-${slot}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { contentType: blob.type, cacheControl: "31536000" });
  if (error) throw error;
  return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
}

/** Borra el archivo del almacenamiento si la URL es de nuestro bucket (mejor esfuerzo). */
export async function removeEventImageFile(url: string): Promise<void> {
  const marker = `/${BUCKET}/`;
  const i = url.indexOf(marker);
  if (i === -1) return;
  const path = decodeURIComponent(url.slice(i + marker.length).split("?")[0]);
  await supabase.storage.from(BUCKET).remove([path]).catch(() => undefined);
}

/** La foto del evento y su forma real (cuadrada, horizontal o vertical). */
export function eventImage(e: Pick<CalEvent, "imageUrl" | "imageVerticalUrl" | "imageSquareUrl">): { url: string; shape: ImageSlot } | null {
  if (e.imageSquareUrl) return { url: e.imageSquareUrl, shape: "square" };
  if (e.imageUrl) return { url: e.imageUrl, shape: "horizontal" };
  if (e.imageVerticalUrl) return { url: e.imageVerticalUrl, shape: "vertical" };
  return null;
}
